<template lang="pug">
    .chart
        div(ref='chart' class="chart")
        slot(name="options")
</template>

<script>

import { options, create, useTheme, color } from '@amcharts/amcharts4/core'
import { XYChart, XYCursor, DateAxis, ValueAxis, ColumnSeries } from '@amcharts/amcharts4/charts'
import am4themes_animated from '@amcharts/amcharts4/themes/animated'
import am4themes_theme from '@amcharts/amcharts4/themes/dataviz'
import am4lang_ru_RU from './ru_RU'
import am4lang_ua_UA from './ua_UA'
import am4lang_en_US from './en_US'
import { mapState, mapGetters } from 'vuex'

options.onlyShowOnViewport = true
options.queue = true

useTheme(am4themes_animated)
useTheme(am4themes_theme)

export default {
    props: {
        data: {
            type: Array,
            required: true
        },
        date: {
            type: String,
            required: true
        },
        customTitle: {
            type: String,
            required: true
        },
        tooltip: {
            type: String,
            default: ''
        },
        chartType: {
            type: String,
            default: null
        }
    },
    data: () => ({
        dateFormat: {
            day: 'd eeee',
            month: 'MMMM'
        },
        languages: {
            ru: am4lang_ru_RU,
            ua: am4lang_ua_UA,
            en: am4lang_en_US
        },
        series: []
    }),
    computed: {
        ...mapGetters(['user']),
        ...mapState({
            language: (state) => state.app.language
        })
    },
    watch: {
        data: function(data) {
            this.chart.data = data
        },
        date: function(date) {
            const format = date === 'week' ? 'day' : date

            this.dateAxis.dateFormats.setKey(format, this.dateFormat[format])
            this.dateAxis.periodChangeDateFormats.setKey(format, this.dateFormat[format])

            this.series.forEach(series => {
                series.dataFields.dateX = date
            })
        },
        language: function(lang) {
            this.chart.language.locale = this.languages[lang]
            this.title.text = this.customTitle
        }
    },
    mounted: function() {
        if (this.chart) {
            this.chart.dispose()
        }

        const chart = create(this.$refs.chart, XYChart)
        options.autoSetClassName = true
        chart.hiddenState.properties.opacity = 0
        const cursor = new XYCursor()
        chart.paddingRight = 20
        chart.data = this.data
        chart.preloader.disabled = false

        // date X
        const dateAxis = chart.xAxes.push(new DateAxis())
        dateAxis.dateFormats.setKey(this.date, this.dateFormat[this.date])
        dateAxis.periodChangeDateFormats.setKey(this.date, this.dateFormat[this.date])
        dateAxis.renderer.grid.template.strokeOpacity = 0
        dateAxis.renderer.minGridDistance = 20
        dateAxis.tooltip.disabled = true

        this.dateAxis = dateAxis
        // value Y
        const valueAxis = chart.yAxes.push(new ValueAxis())
        valueAxis.min = 0

        // mobile
        dateAxis.renderer.labels.template.rotation = 270
        dateAxis.renderer.labels.template.verticalCenter = 'middle'
        cursor.behavior = 'none'
        chart.zoomOutButton.disabled = true

        // series
        if (this.chartType === 'partners') {
            const total = this.generateSeries({
                field: 'total',
                tooltipText: `{valueY} ${this.tooltip}`,
                stacked: true
            })

            chart.series.push(total)
            this.series.push(total)
        } else {
            const online = this.generateSeries({
                field: 'online',
                tooltipText: `Online: {valueY} ${this.tooltip}`,
                stacked: true
            })

            const offline = this.generateSeries({
                field: 'offline',
                tooltipText: `Offline: {valueY} ${this.tooltip}`,
                stacked: true,
                columnColor: '#D195FF'
            })

            chart.series.push(online)
            chart.series.push(offline)

            this.series.push(online)
            this.series.push(offline)
        }

        chart.cursor = cursor
        chart.language.locale = this.languages[this.language]

        const title = chart.titles.create()
        title.text = this.customTitle
        title.marginBottom = 30
        title.marginTop = 18
        title.align = 'center'
        title.fontSize = 22
        this.title = title
        this.chart = chart
    },
    beforeDestroy() {
        if (this.chart) {
            this.chart.dispose()
        }
    },
    methods: {
        generateSeries(options) {
            const { field, tooltipText, stacked, columnColor } = options

            const series = new ColumnSeries()
            const columnTemplate = series.columns.template

            // Setup series
            series.dataFields.valueY = field
            series.sequencedInterpolation = false
            series.stacked = stacked
            series.tooltipText = tooltipText
            series.dataFields.dateX = this.date

            if (!this.user.includeTelephony) {
                series.tooltip.pointerOrientation = 'vertical'
            }

            // Setup column
            columnTemplate.width = 25
            if (columnColor) {
                columnTemplate.fill = color(columnColor)
                columnTemplate.stroke = color(columnColor)
            }

            return series
        }
    }
}
</script>

<style scoped>
    .chart {
        height: 430px;
        width: 100%;
    }

    .chart ::v-deep g[aria-labelledby] {
        display: none!important;
    }

    @media only screen and (max-width: 1000px) {
        .chart {
            margin-bottom: 40px;
            width: 100%;
        }
    }
</style>
